// src/lib/hooks/useLocationSearch.ts
import debounce from "lodash/debounce";
import { useState } from "react";

// src/lib/types/location.ts
export interface LocationOption {
  label: string;
  value: string;
  place_id: string;
}

export interface FormattedLocation {
  city: string;
  state: string | null;
  country: string | null;
  formatted_address: string;
  /**
   * Google's `formattedAddress` from place details, with postal code and unit
   * ("350 5th Ave apt 4b, Brooklyn, NY 11215, USA"). `formatted_address` holds the
   * autocomplete suggestion text instead, which never has a postal code. Present only
   * once place details resolve.
   */
  full_address?: string;
  display_name?: string; // Short name for pills (e.g., "Google Chicago Fulton" instead of full address)
  place_id: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  /**
   * Map-viewport bounding box for the place ({ne, sw}), present only for geographic places
   * (city/region/country) that carry a Google Places `viewport`. Drives "zoom to fit" so a
   * country search frames the whole country instead of a fixed zoom-12 box. Null/absent for
   * addresses, hotels, and places Google returns without a viewport — those fall back to
   * center + zoom.
   */
  viewport?: { ne: { lat: number; lng: number }; sw: { lat: number; lng: number } } | null;
  type?: "city" | "address" | "enterprise_office";
  pending?: boolean;
  /**
   * Our DB venue id when the picked place is a hotel — looked up (and created from its Google
   * listing if needed) on selection by the sourcing search's `createHotelVenue`, which posts to
   * its `HOTEL_VENUE_ENDPOINT` (named there so this doc can't drift from the route). Null/undefined
   * for non-hotel places or if the lookup failed. Downstream proposal flows read this to reference
   * the hotel.
   */
  venueStaticId?: number | null;
  /**
   * True when the hotel pick created `venueStaticId` just now from its Google listing, rather than
   * finding a venue we already had. Undefined for non-hotel places or a failed lookup.
   */
  venueCreated?: boolean;
}

/**
 * The address to display/store for a location — `formatted_address`, falling back to `city` for
 * legacy `formatted_locations` records that predate `formatted_address`/`display_name`.
 */
export function locationValue(loc: FormattedLocation): string {
  return loc.formatted_address || loc.city;
}

export function useLocationSearch(isGoogleMapsLoaded: boolean) {
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchLocations = debounce(async (input: string) => {
    if (!isGoogleMapsLoaded || !input || input.length < 2) {
      setLocationOptions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch("/api/places/autocomplete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          input,
          includedPrimaryTypes: ["(regions)"],
        }),
      });

      const data = await response.json();
      const suggestions = data.suggestions || [];

      // Map suggestions to LocationOptions
      const options: LocationOption[] = suggestions
        .slice(0, 10)
        .map((suggestion: any): LocationOption | null => {
          const placePrediction = suggestion.placePrediction;
          if (!placePrediction?.placeId || !placePrediction?.text?.text) {
            return null;
          }
          return {
            label: placePrediction.text.text,
            value: placePrediction.text.text,
            place_id: placePrediction.placeId,
          };
        })
        .filter((option: LocationOption | null): option is LocationOption => option !== null);

      // Deduplicate by place_id
      const uniqueOptions: LocationOption[] = Array.from(
        new Map(options.map((option) => [option.place_id, option])).values()
      );

      setLocationOptions(uniqueOptions);
    } catch (error) {
      console.error("Error fetching places:", error);
      setLocationOptions([]);
    } finally {
      setIsSearching(false);
    }
  }, 300);

  return { locationOptions, isSearching, searchLocations };
}
